import React, { useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Dropdown, Button, ButtonToolbar } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';

import {
  toggleInvalidData,
  toggleGraphSync,
  toggleShowGraphLegend,
  shouldShowInvalidData,
  shouldSyncGraphs,
  setViewableEndDate,
  setGraphViewId,
  shouldShowGraphLegends,
} from '@stores/graphViewPageSlice';

import Authorize from '@components/Authorize';
import { clearWeatherStation, setDataRange, setEndDate, setStartDate } from '@stores/dataVisualizationSlice';
import { setSeconds, setHours, setMinutes, subDays } from 'date-fns';
import { getWxLoadingStatus } from '@stores/weatherStationSlice';
import { STATE_STATUS, DATA_RANGE, Views, Permissions } from '@awp/constants';

const PageActions = () => {
  const weatherLoading = useSelector(getWxLoadingStatus);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const showInvalidData = useSelector(shouldShowInvalidData);
  const showGraphLegends = useSelector(shouldShowGraphLegends);
  const syncGraphs = useSelector(shouldSyncGraphs);

  const toggleInvalidDataSelector = useCallback(() => {
    dispatch(toggleInvalidData());
  }, []);

  const toggleGraphSyncSelector = useCallback(() => {
    dispatch(toggleGraphSync());
  }, []);

  const toggleShowGraphLegendSelector = useCallback(() => {
    dispatch(toggleShowGraphLegend());
  }, []);

  const openValidationModal = () => {
    navigate(Views.ManualDataValidation, { state: { referrer: Views.SingleStationGraph } });
  };

  const clearAll = () => {
    dispatch(clearWeatherStation());
    dispatch(setGraphViewId(null));
    dispatch(setDataRange({ value: DATA_RANGE.DEFAULT }));

    dispatch(
      setViewableEndDate({
        value: new Date().getTime(),
        error: '',
        disabled: false,
      }),
    );

    dispatch(
      setEndDate({
        value: new Date().getTime(),
        error: '',
        disabled: false,
      }),
    );

    dispatch(
      setStartDate({
        value: setSeconds(setMinutes(setHours(subDays(new Date(), DATA_RANGE.DEFAULT), 0), 0), 0).getTime(), //wiped seconds and reset to 00
        error: '',
        disabled: false,
      }),
    );
  };

  return (
    <ButtonToolbar className="justify-content-end">
      <Button variant="outline-secondary" className="mr-2" onClick={clearAll}>
        Clear all
      </Button>
      <Dropdown>
        <Dropdown.Toggle variant="primary" id="dropdown_actions_id" disabled={weatherLoading !== STATE_STATUS.SUCCESS}>
          Actions
        </Dropdown.Toggle>
        <Dropdown.Menu style={{ width: '18rem' }}>
          <Authorize requires={[Permissions.WriteGraphManualValidation]}>
            <Dropdown.Item onClick={openValidationModal}>Manual Data Validation</Dropdown.Item>
            <Dropdown.Divider />
          </Authorize>
          <Dropdown.ItemText onClick={toggleInvalidDataSelector}>
            <i className={`bi ${showInvalidData ? 'bi-check-square text-success' : 'bi-square'} mr-2`}></i>
            Show invalid data
          </Dropdown.ItemText>
          <Dropdown.ItemText onClick={toggleShowGraphLegendSelector}>
            <i className={`bi ${showGraphLegends ? 'bi-check-square text-success' : 'bi-square'} mr-2`}></i>
            Show graph legends
          </Dropdown.ItemText>
          <Dropdown.Divider />
          <Dropdown.ItemText onClick={toggleGraphSyncSelector}>
            <i className={`bi ${syncGraphs ? 'bi-check-square text-success' : 'bi-square'} mr-2`}></i>
            Synchronize graphs
          </Dropdown.ItemText>
        </Dropdown.Menu>
      </Dropdown>
    </ButtonToolbar>
  );
};

export default PageActions;
